import { useEffect, useState } from 'react';

import { ReactComponent as DeleteSVG } from '../../../assets/icons/delete.svg';
import { ReactComponent as UpdateSVG } from '../../../assets/icons/refresh.svg';
import { AdditionalRequirement } from '../../../components/ClientCreatedJobDetails';
import { FaFilePdf, FaDownload, FaExternalLinkAlt } from 'react-icons/fa';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import ClientInformation from '../../../components/PrinterBrowsedJobDetails/ClientInformation';
import JobInformation from '../../../components/PrinterBrowsedJobDetails/JobInformation';
import PdfInformation from '../../../components/PrinterBrowsedJobDetails/PdfInformation';
import PropertyInformation from '../../../components/PrinterBrowsedJobDetails/PropertyInformation';
import RequirementInformation from '../../../components/PrinterBrowsedJobDetails/RequirementInformation';
import CostAndShipping from '../../../components/TechnicianBrowsedJobDetails/CostAndShipping';
import {
  Button,
  Divider,
  Modal,
  NavigateBack,
  Typography,
} from '../../../designComponents';
import { ActionsContainer } from '../../CreateJobs/ViewCreatedJobs/style';
import { PrinterDetailsContainerStyled } from './style';
import { requestAPI, humanReadableDate } from '../../../utils';
import printerApi from '../../../APIs/printer';
import { useParams } from 'react-router-dom';
import SelectDropDown from '../../../designComponents/SelectDropdown';
import { toast } from 'react-toastify';
import { useRoot } from '../../../context/RootProvider';
import Tag from '../../../designComponents/status';

type ClientType = {
  _id: string;
  emailAddress: string;
};

type PrinterJobDetailsType = {
  _id: string;
  clientId: ClientType;
  jobTitle: string;
  notes: string;
  printerId: string;
  pdf: any[];
  shippingAddress: string;
  additionalRequirement: any[];
  printerJobStatus: string;
  printerJobStatusChangedDate: any[];
  createdAt: string;
  updatedAt: string;
};

type PrinterType = {
  user: string;
  fullName: string;
};

type PrinterOptionType = {
  label: string;
  value: string;
};

const PrinterJobDetails = () => {
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const { auth } = useRoot();
  const role = auth?.role;
  const myId = auth?.user;
  const [jobDetails, setJobDetails] = useState<PrinterJobDetailsType | null>(
    null
  );
  const { jobId } = useParams();

  const statusOptions = [
    { label: 'DELIVERED', value: 'DELIVERED' },
    { label: 'PENDING', value: 'PENDING' },
    { label: 'ONTHEWAY', value: 'ONTHEWAY' },
    { label: 'COMPLETED', value: 'COMPLETED' },
  ];

  const getPrinterJobWithId = async (jobId: string) => {
    try {
      const response = await requestAPI(printerApi.getPrinterJobById(jobId));
      const data = response;
      setJobDetails(data);
    } catch (error) {
      console.error('Error fetching job details:', error);
    }
  };

  const handleStatusChange = async (id: string) => {
    if (!selectedStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      await requestAPI(
        printerApi.updatePrinterJobStatus(id, { status: selectedStatus })
      );
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      setSelectedStatus('');
      // Refresh job details
      getPrinterJobWithId(id);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  useEffect(() => {
    if (jobId) {
      getPrinterJobWithId(jobId);
    }
  }, [jobId]);

  if (!jobDetails) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Typography variant="title">Loading...</Typography>
      </div>
    );
  }

  const getStatusType = (status: string) => {
    switch (status) {
      case 'REQUESTED':
        return 'warning';
      case 'ACCEPTED':
      case 'DELIVERED':
      case 'COMPLETED':
        return 'success';
      case 'REJECTED':
        return 'danger';
      case 'PENDING':
        return 'warning';
      case 'ONTHEWAY':
        return 'info';
      default:
        return 'success';
    }
  };

  const canChangeStatus = role === 'PRINTER' && myId === jobDetails.printerId;

  return (
    <PrinterDetailsContainerStyled>
      {/* Sticky Header */}
      <div
        style={{
          position: 'sticky',
          top: '60px',
          zIndex: '999',
          paddingTop: '5px',
          backgroundColor: '#fff',
          paddingBottom: '10px',
        }}
      >
        <NavigateBack />
      </div>

      {/* Main Content */}
      <div className="container">
        {/* Header Section */}
        <div
          className="heading"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div style={{ flex: '1', minWidth: '200px' }}>
            <Typography variant="title" className="heading">
              {jobDetails.jobTitle}
            </Typography>
            <Typography style={{ color: '#666', marginTop: '8px' }}>
              Created on {humanReadableDate(jobDetails.createdAt)}
            </Typography>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Tag
              type={getStatusType(jobDetails.printerJobStatus)}
              style={{ padding: '6px 16px', fontSize: '14px' }}
            >
              {jobDetails.printerJobStatus}
            </Tag>
            {canChangeStatus && (
              <Button
                variant="primary"
                onClick={() => setShowStatusModal(true)}
              >
                Change Status
              </Button>
            )}
          </div>
        </div>

        <Divider spaceY="24px" />

        {/* Job Information Section */}
        <section style={{ marginBottom: '32px' }}>
          <Typography style={{ marginBottom: '16px', fontWeight: '600' }}>
            Job Details
          </Typography>
          <JobInformation
            jobCreatedAt={humanReadableDate(jobDetails.createdAt)}
            jobTitle={jobDetails.jobTitle}
            notes={jobDetails.notes}
          />
        </section>

        <Divider spaceY="24px" />

        {/* Additional Requirements Section */}
        {jobDetails.additionalRequirement &&
          jobDetails.additionalRequirement.length > 0 && (
            <>
              <section style={{ marginBottom: '32px' }}>
                <Typography style={{ marginBottom: '16px', fontWeight: '600' }}>
                  Additional Requirements
                </Typography>
                <AdditionalRequirement
                  additionalInfo={jobDetails.additionalRequirement}
                />
              </section>
              <Divider spaceY="24px" />
            </>
          )}

        {/* PDF Documents Section - Enhanced */}
        <section style={{ marginBottom: '32px' }}>
          <Typography style={{ marginBottom: '16px', fontWeight: '600' }}>
            Documents ({jobDetails.pdf?.length || 0})
          </Typography>

          {jobDetails.pdf && jobDetails.pdf.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gap: '16px',
              }}
            >
              {jobDetails.pdf.map((pdf, index) => (
                <div
                  key={index}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#fafafa',
                    transition: 'box-shadow 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* PDF Header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#E53935',
                        borderRadius: '6px',
                        padding: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FaFilePdf size={24} color="#fff" />
                    </div>

                    <div style={{ flex: 1 }}>
                      <Typography
                        variant="normal"
                        style={{ fontWeight: '600', marginBottom: '4px' }}
                      >
                        {pdf.name || `Document ${index + 1}`}
                      </Typography>
                      <Typography style={{ color: '#666' }}>
                        Quantity: {pdf.quantity || '1'}
                      </Typography>
                    </div>

                    {/* Action Buttons */}
                    {pdf.url && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          variant="secondary"
                          onClick={() => window.open(pdf.url, '_blank')}
                          style={{
                            padding: '8px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <FaExternalLinkAlt size={14} />
                          View
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = pdf.url;
                            link.download = pdf.name || 'document.pdf';
                            link.click();
                          }}
                          style={{
                            padding: '8px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <FaDownload size={14} />
                          Download
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* PDF Notes */}
                  {pdf.notes && (
                    <div
                      style={{
                        backgroundColor: '#fff',
                        padding: '12px',
                        borderRadius: '6px',
                        borderLeft: '3px solid #2196F3',
                      }}
                    >
                      <Typography
                        style={{
                          color: '#666',
                          fontWeight: '600',
                          marginBottom: '4px',
                        }}
                      >
                        Notes:
                      </Typography>
                      <Typography variant="normal" style={{ color: '#333' }}>
                        {pdf.notes}
                      </Typography>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                border: '2px dashed #e0e0e0',
                borderRadius: '8px',
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#fafafa',
              }}
            >
              <FaFilePdf
                size={48}
                color="#ccc"
                style={{ marginBottom: '12px' }}
              />
              <Typography variant="normal" style={{ color: '#999' }}>
                No documents available
              </Typography>
            </div>
          )}
        </section>

        {/* Status Change Modal */}
        <Modal
          open={showStatusModal}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedStatus('');
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '32px',
              minWidth: '400px',
            }}
          >
            <div>
              <Typography variant="title" style={{ marginBottom: '8px' }}>
                Change Job Status
              </Typography>
              <Typography style={{ color: '#666' }}>
                Update the current status of this job
              </Typography>
            </div>

            <div>
              <Typography
                variant="normal"
                style={{ marginBottom: '8px', fontWeight: '500' }}
              >
                Current Status:{' '}
                <Tag type={getStatusType(jobDetails.printerJobStatus)}>
                  {jobDetails.printerJobStatus}
                </Tag>
              </Typography>
            </div>

            <div>
              <Typography
                variant="normal"
                style={{ marginBottom: '8px', fontWeight: '500' }}
              >
                New Status
              </Typography>
              <SelectDropDown
                selected={selectedStatus}
                options={statusOptions}
                onSelect={(value: string) => {
                  setSelectedStatus(value);
                }}
                placeholder="Select new status"
              />
            </div>

            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                paddingTop: '16px',
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Button
                variant="secondary"
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedStatus('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  handleStatusChange(jobDetails._id);
                }}
                disabled={!selectedStatus}
              >
                Update Status
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </PrinterDetailsContainerStyled>
  );
};

export default PrinterJobDetails;
